import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d94m4g5568obap8dbbp0';
const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbxc1JzGF4f83ttjys_WVUzpS15gTPHj-LVUb96PNZ4vTh7-UPESpIcqsgQ_V7bu49Fc/exec';

export default function ContactForm() {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const serviceOptions = [
    { value: '', key: 'contact.servicePlaceholder' },
    { value: 'marketing-strategy', key: 'contact.serviceStrategy' },
    { value: 'meta-ads', key: 'contact.serviceMetaAds' },
    { value: 'automation', key: 'contact.serviceAutomation' },
    { value: 'web-design', key: 'contact.serviceWebDesign' },
    { value: 'traffic-pro', key: 'contact.serviceTrafficPro' },
    { value: 'traffic-master', key: 'contact.serviceTrafficMaster' },
    { value: 'mentoring', key: 'contact.serviceMentoring' },
    { value: 'other', key: 'contact.serviceOther' },
  ];

  const contactInfo = [
    { icon: 'ri-whatsapp-line', labelKey: 'contact.whatsapp', value: '+57 3054692336' },
    { icon: 'ri-mail-line', labelKey: 'contact.email', value: 'santiospinamarketer@gmail.com' },
    { icon: 'ri-map-pin-line', labelKey: 'contact.location', valueKey: 'contact.locationValue' },
  ];

  const countryCodes = [
    { code: '+57', label: '🇨🇴 +57' },
    { code: '+52', label: '🇲🇽 +52' },
    { code: '+51', label: '🇵🇪 +51' },
    { code: '+56', label: '🇨🇱 +56' },
    { code: '+54', label: '🇦🇷 +54' },
    { code: '+593', label: '🇪🇨 +593' },
    { code: '+1', label: '🇺🇸 +1' },
    { code: '+34', label: '🇪🇸 +34' },
    { code: '+507', label: '🇵🇦 +507' },
    { code: '+506', label: '🇨🇷 +506' },
    { code: '+58', label: '🇻🇪 +58' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    setStatus('loading');
    setErrorMsg('');

    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      form.reset();
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    const countryCode = (formData.get('countryCode') as string || '+57').trim();
    const phoneNumber = (formData.get('phone') as string || '').trim();

    const submissionData = {
      name: (formData.get('name') as string || '').trim(),
      email: (formData.get('email') as string || '').trim(),
      company: (formData.get('company') as string || '').trim() || null,
      phone: phoneNumber ? `${countryCode} ${phoneNumber}` : null,
      service: (formData.get('service') as string || '').trim() || null,
      message: (formData.get('message') as string || '').trim() || null,
    };

    try {
      // Save to Supabase
      const { error: supabaseError } = await supabase
        .from('contact_submissions')
        .insert([submissionData]);

      if (supabaseError) {
        console.error('Supabase insert error:', supabaseError);
      }

      // Save to Google Sheets
      fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      }).catch(() => {});

      // Also submit to Readdy Forms
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || 'Something went wrong.';
        setErrorMsg(serverMsg);
        setStatus('error');
      }
    } catch {
      setErrorMsg(t('contact.networkError'));
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-start">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('contact.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {t('contact.title')}{' '}
              <span className="text-primary-500">{t('contact.titleHighlight')}</span>{' '}
              {t('contact.subtitle')}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-md">
              {t('contact.contactSubtitle')}
            </p>

            <div className="space-y-5">
              {contactInfo.map((item) => (
                <div key={item.labelKey} className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center text-red-600 flex-shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">{t(item.labelKey)}</div>
                    <div className="text-gray-800 font-medium text-sm">
                      {item.valueKey ? t(item.valueKey) : item.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
            <h3 className="font-heading text-xl font-semibold text-gray-900 mb-6">
              {t('contact.formTitle')}
            </h3>

            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">{t('contact.fullName')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    placeholder={t('contact.namePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">{t('contact.emailLabel')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    placeholder={t('contact.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">{t('contact.company')}</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    placeholder={t('contact.companyPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">{t('contact.phone')}</label>
                  <div className="flex gap-2">
                    <select
                      id="countryCode"
                      name="countryCode"
                      defaultValue="+57"
                      className="px-3 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all appearance-none cursor-pointer flex-shrink-0 w-[120px]"
                    >
                      {countryCodes.map((cc) => (
                        <option key={cc.code} value={cc.code}>{cc.label}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="flex-1 px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                      placeholder={t('contact.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1.5">{t('contact.service')}</label>
                  <select
                    id="service"
                    name="service"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all appearance-none cursor-pointer"
                  >
                    {serviceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{t(opt.key)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">{t('contact.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all resize-none"
                    placeholder={t('contact.messagePlaceholder')}
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1">{t('contact.charLimit')}</p>
                </div>

                <div className="honeypot-field" aria-hidden="true">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? t('contact.sending') : t('contact.submit')}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>

              {status === 'success' && (
                <div className="mt-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm text-center">
                  {t('contact.success')}
                </div>
              )}

              {status === 'error' && errorMsg && (
                <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                  {errorMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}